import React, { useState } from "react";
import UploadNew from "./addProduct/UploadPdf";
import CategoriesSelection from "./addProduct/CategoriesSelection";
import PublishVideos from "./addVideo/PublishVideos";
import { useAuth } from "../../../../../firebase/AuthContext";

const colorMap = {
  orange: {
    bg: "bg-orange-500/10",
    border: "hover:border-orange-500/50",
    iconText: "text-orange-500",
    ring: "hover:ring-orange-500/20",
  },
  blue: {
    bg: "bg-blue-500/10",
    border: "hover:border-blue-500/50",
    iconText: "text-blue-500",
    ring: "hover:ring-blue-500/20",
  },
  // green: {
  //     bg: "bg-green-500/10",
  //     border: "hover:border-green-500/50",
  //     iconText: "text-green-500",
  //     ring: "hover:ring-green-500/20",
  // },
};

/**
 * UploadTab — owns all sub-navigation and local state for the Upload tab.
 *
 * Sub-views:
 *   "menu"   → inline menu (choose between PDF upload or videos)
 *   "pdf"    → UploadNew   (multi-step PDF upload & product creation)
 *   "videos" → PublishVideos
 *
 * UpdatePage only needs to render <UploadTab language={language} currentUser={currentUser} />
 */
function UploadTab({ language, currentUser }) {
  const { hasRole } = useAuth();
  const [subView, setSubView] = useState("menu");
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null); // { categoryId, categoryName, subCategoryId, subCategoryName }

  const options = [
    {
      id: "upload-pdf",
      role: "product.create",
      titleEn: "Upload Product PDF",
      titleZh: "上傳產品 PDF",
      descEn:
        "Upload a product datasheet PDF to extract and create product information automatically using AI.",
      descZh: "上傳產品規格書 PDF，透過 AI 自動提取並建立產品資訊。",
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
          />
        </svg>
      ),
      color: "orange",
      onClick: () => setSubView("categories"),
    },
    {
      id: "publish-videos",
      role: "knowledge.video.publish",
      titleEn: "Publish Videos",
      titleZh: "發佈影片",
      descEn:
        "Upload and manage product demonstration videos, tutorials, and promotional content.",
      descZh: "上傳並管理產品示範影片、教學影片及宣傳內容。",
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      ),
      color: "blue",
      onClick: () => setSubView("videos"),
    },
  ];

  if (subView === "categories") {
    return (
      <CategoriesSelection
        language={language}
        onBack={() => setSubView("menu")}
        onNext={(categorySelection) => {
          setSelectedCategory(categorySelection);
          setSubView("pdf");
        }}
      />
    );
  }

  if (subView === "pdf") {
    return (
      <UploadNew
        language={language}
        currentUser={currentUser}
        file={file}
        setFile={setFile}
        status={status}
        setStatus={setStatus}
        loading={loading}
        setLoading={setLoading}
        selectedCategory={selectedCategory}
        onBack={() => {
          setSubView("categories");
          setFile(null);
          setStatus("");
        }}
      />
    );
  }

  if (subView === "videos") {
    return (
      <PublishVideos language={language} onBack={() => setSubView("menu")} />
    );
  }

  // "menu" view — inline (was UploadMenu)
  return (
    <div className="animate-slideUp flex justify-center">
      <div className="w-full max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {options
            .filter((option) => hasRole(option.role))
            .map((option) => {
              const colors = colorMap[option.color];
              return (
                <button
                  key={option.id}
                  onClick={option.onClick}
                  className={`group bg-white border-2 border-gray-200 ${colors.border} rounded-2xl p-8 text-left transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:ring-4 ${colors.ring} focus:outline-none focus:ring-4 ${colors.ring}`}
                >
                  {/* Icon */}
                  <div
                    className={`w-16 h-16 ${colors.bg} rounded-2xl flex items-center justify-center mb-5 ${colors.iconText} group-hover:scale-110 transition-transform duration-300`}
                  >
                    {option.icon}
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {language === "zh" ? option.titleZh : option.titleEn}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-gray-500 leading-relaxed mb-4">
                    {language === "zh" ? option.descZh : option.descEn}
                  </p>

                  {/* CTA arrow */}
                  <div
                    className={`inline-flex items-center gap-2 text-sm font-medium ${colors.iconText}`}
                  >
                    {language === "zh" ? "開始" : "Get started"}
                    <svg
                      className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </button>
              );
            })}
        </div>
      </div>
    </div>
  );
}

export default UploadTab;
